import * as React from "react";
import { UserRound } from "lucide-react";
import { cn } from "@/lib/cn";
import { isDeletedUser } from "@/lib/users";

function initials(nameOrEmail: string): string {
  return (
    nameOrEmail
      .trim()
      .split(/\s+/)
      .slice(0, 2)
      .map((part) => part[0]?.toUpperCase() ?? "")
      .join("") || "?"
  );
}

/**
 * A stable pick across `avatar-1`..`avatar-6` (`ui.css`'s `avatar--tint-*`,
 * `design-tokens.json`'s `avatarNote`) so the same person always lands on the
 * same letter tile, on every screen and every reload. Not cryptographic -
 * just deterministic and roughly even across six buckets, which is all a
 * fallback tile needs.
 */
function hashToTint(value: string): number {
  let hash = 0;
  for (let i = 0; i < value.length; i++) {
    hash = (hash << 5) - hash + value.charCodeAt(i);
    hash |= 0;
  }
  return (Math.abs(hash) % 6) + 1;
}

export type PersonLike = {
  id?: string | null;
  full_name?: string | null;
  email?: string | null;
  avatar_url?: string | null;
};

export function PersonAvatar({
  person,
  fallbackName = "Unknown",
  tone = "neutral",
  size = "sm",
  className,
}: {
  person: PersonLike | null | undefined;
  fallbackName?: string;
  tone?: "accent" | "neutral" | "panel";
  size?: "xs" | "sm" | "md" | "lg";
  className?: string;
}) {
  const name = person?.full_name ?? person?.email ?? fallbackName;
  const src = person?.avatar_url;
  const deleted = isDeletedUser(person?.full_name);
  const classes = cn("avatar", `avatar--${tone}`, `avatar--${size}`, className);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => setFailed(false), [src]);

  if (deleted) {
    return (
      <span aria-hidden className={classes}>
        <UserRound />
      </span>
    );
  }

  if (src && !failed) {
    return (
      <img src={src} alt="" loading="lazy" className={classes} onError={() => setFailed(true)} />
    );
  }

  // `panel` sits on the near-black nav rail, in both themes - a tinted tile
  // there would need to be legible against a background the ordinary
  // light/dark avatar colours were never chosen for, same reason
  // `avatar--panel` exists at all. Everywhere else, letters get a colour of
  // their own instead of blending into the row around them.
  const tint = tone === "panel" ? null : hashToTint(person?.id ?? person?.email ?? name);
  const initialsClasses = cn(
    "avatar",
    tint ? `avatar--tint-${tint}` : `avatar--${tone}`,
    `avatar--${size}`,
    className
  );

  return (
    <span aria-hidden className={initialsClasses}>
      {initials(name)}
    </span>
  );
}
